import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ArrowRight, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { QuietBtn, StatusPill } from "@/pages/admin/panel/ui";
import { Stepper, DOT } from "./Stepper";
import { StageView, STAGE_ORDER } from "./StageView";

export function CertFlow() {
  const { id, stage } = useParams();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [hub, setHub] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get(`/admin/certs/${id}/panel`), api.get(`/admin/certs/${id}/hub`)])
      .then(([p, h]) => { setPanel(p.data); setHub(h.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, [id]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  const approve = async () => {
    try {
      const { data } = await api.post(`/admin/certs/${id}/approve`);
      toast.success(`Published — ${data.sources_retained} sources retained (admin-only), Stripe price ${data.stripe_price_created ? "created" : "MISSING"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const unpublish = async () => {
    try { await api.post(`/admin/certs/${id}/unpublish`); toast.success("Unpublished — landing page and exams are dark; Stripe price left intact"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const deleteCert = async () => {
    const typed = window.prompt(`This permanently wipes EVERYTHING for this cert — questions, videos, jobs, costs, pageviews, sources, entitlements, Spaces assets and local renders.\n\nType the cert slug (${panel.cert.slug}) to confirm:`);
    if (typed == null) return;
    try {
      const { data } = await api.delete(`/admin/certs/${id}`, { params: { confirm: typed } });
      toast.success(`Deleted ${data.slug} — full cascade wiped`); navigate("/admin");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const start = async () => {
    try { await api.post(`/admin/certs/${id}/start`, {}); toast.success("Started — test run first; a green test run starts the full generation by itself"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!panel || !hub) return <p className="text-zinc-500">Loading…</p>;
  const c = panel.cert;
  const current = hub.now.stage?.key;
  const actions = { approve, unpublish, deleteCert, start, reload: load };

  return (
    <div className="fade-in space-y-4" data-testid="control-panel">
      <div className="flex flex-wrap items-center gap-3">
        {stage && <Link to={`/admin/certs/${id}`} className="text-sm text-sky-600 hover:underline flex items-center gap-1" data-testid="back-to-hub"><ArrowLeft size={14} /> Cert hub</Link>}
        <div>
          <h1 className="text-[20px] font-black">{c.name}</h1>
          <p className="text-xs text-zinc-400">{c.vendor} · {c.version_tag} · {c.style_profile} · ${Number(c.price_usd || 0).toFixed(0)}</p>
        </div>
        <StatusPill status={c.status} />
        <div className="ml-auto flex gap-2">
          <QuietBtn data-testid="approve-cert-btn" onClick={approve} disabled={c.status !== "review" || !panel.publish_checklist?.ready}
                    title={panel.publish_checklist?.ready ? "" : panel.publish_checklist?.sentence}>Approve and publish</QuietBtn>
          <QuietBtn danger data-testid="unpublish-cert-btn" onClick={unpublish} disabled={c.status !== "published"}>Unpublish</QuietBtn>
          <QuietBtn danger data-testid="delete-cert-btn" onClick={deleteCert}><Trash2 size={12} /> Delete cert</QuietBtn>
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
        <Stepper certId={id} stages={hub.stages} current={stage || current} />
      </div>
      {stage ? <StageView stage={stage} id={id} panel={panel} hub={hub} actions={actions} />
             : <Hub id={id} panel={panel} hub={hub} actions={actions} navigate={navigate} />}
      {stage && <NextBar id={id} stage={stage} />}
    </div>
  );
}

function Hub({ id, hub, actions, navigate }) {
  const { now, your_steps, machine_handles, blocking } = hub;
  const s = now.stage;
  const failed = s?.status === "failed";
  const onPrimary = () => {
    const [, key, extra] = now.button || [];
    if (extra === "start") return actions.start();
    if (extra === "approve") return actions.approve();
    navigate(`/admin/certs/${id}/stage/${key}${extra && extra !== "start" ? `?job=${extra}` : ""}`);
  };
  return (
    <>
      <section className={`rounded-xl border-2 px-5 py-4 ${failed ? "border-red-300 bg-red-50" : now.needs_you ? "border-amber-300 bg-amber-50" : "border-sky-200 bg-white"}`} data-testid="now-panel">
        <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Now</p>
        <p className="text-lg font-bold mt-0.5" data-testid="now-line-1">Stage {s.n} · {s.name} — {s.line}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className={`text-sm ${failed ? "text-red-700" : "text-zinc-700"}`} data-testid="now-action">{now.action}</p>
          <button data-testid="now-primary-btn" disabled={!now.needs_you} onClick={onPrimary}
                  className={`ml-auto rounded-md px-4 py-2 text-sm font-bold transition-colors duration-150 ${now.needs_you ? "bg-sky-500 text-white hover:bg-sky-600" : "bg-zinc-100 text-zinc-400 cursor-not-allowed"}`}>
            {now.button?.[0] || "Nothing to do"}
          </button>
        </div>
      </section>
      <div className="grid sm:grid-cols-2 gap-4">
        <section className="bg-white border border-zinc-200 rounded-xl px-5 py-4" data-testid="your-steps">
          <h2 className="text-sm font-bold mb-2">Your steps</h2>
          <ol className="space-y-1.5">
            {your_steps.map((st, i) => (
              <li key={st.key}>
                <Link to={`/admin/certs/${id}/stage/${st.key}`} className="flex items-center gap-2 text-sm hover:underline" data-testid={`your-step-${st.key}`}>
                  <span className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] ${st.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-zinc-300"}`}>{st.done ? "✓" : ""}</span>
                  <span className={st.done ? "text-zinc-400 line-through" : "text-zinc-800"}>{i + 1}. {st.label}</span>
                </Link>
              </li>
            ))}
          </ol>
        </section>
        <section className="bg-white border border-zinc-200 rounded-xl px-5 py-4" data-testid="machine-handles">
          <h2 className="text-sm font-bold mb-2">Machine handles</h2>
          <ul className="grid grid-cols-1 gap-1.5">
            {machine_handles.map((m) => (
              <li key={m.key} className="flex items-center gap-2 text-sm text-zinc-700" data-testid={`machine-${m.key}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${DOT[m.status] || DOT.idle}`} />{m.label}
                <span className="ml-auto text-xs text-zinc-400">{m.status === "idle" ? "" : m.status}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <p className="text-sm" data-testid="blocking-summary">
        {blocking.live ? <span className="text-emerald-700 font-semibold">Live.</span>
          : blocking.ready ? <span className="text-emerald-700 font-semibold">Ready to publish.</span>
          : <Link to={`/admin/certs/${id}/stage/publish`} className="text-amber-700 font-semibold hover:underline flex items-center gap-1"><AlertTriangle size={14} /> Not ready to publish — {blocking.count} blocking</Link>}
      </p>
    </>
  );
}

function NextBar({ id, stage }) {
  const i = STAGE_ORDER.findIndex((s) => s.key === stage);
  const next = STAGE_ORDER[i + 1];
  return (
    <div className="flex justify-end pt-2" data-testid="next-stage-bar">
      {next ? <Link to={`/admin/certs/${id}/stage/${next.key}`} className="text-sm font-semibold text-sky-600 hover:underline flex items-center gap-1">Next: Stage {i + 2} · {next.name} <ArrowRight size={14} /></Link>
            : <Link to={`/admin/certs/${id}`} className="text-sm font-semibold text-sky-600 hover:underline flex items-center gap-1"><ArrowLeft size={14} /> Back to cert hub</Link>}
    </div>
  );
}
